import { type ErrorComponentProps, Link, useRouter } from "@tanstack/react-router";
import { AlertTriangle } from "lucide-react";
import { common } from "../../copy/common";
import { errors } from "../../copy/errors";
import { useEffect } from "react";
import { isApiError, isTransientError } from "../../lib/api";
import { buttonStyles } from "../ui/button-styles";
import { FullPage } from "./full-page";
import { Reconnecting } from "./reconnecting";

/** 10c. Something went wrong. Shows the request ID as a support reference. */
export function ErrorPage({ error, reset }: ErrorComponentProps) {
  const router = useRouter();
  const transient = isTransientError(error);

  // Briefly unreachable is not "something went wrong": keep retrying quietly.
  useEffect(() => {
    if (!transient) return;
    const t = setInterval(() => {
      reset();
      void router.invalidate();
    }, 4000);
    return () => clearInterval(t);
  }, [transient, reset, router]);

  if (transient) return <Reconnecting fill />;
  const requestId = isApiError(error) ? error.requestId : null;
  return (
    <FullPage icon={<AlertTriangle className="size-6" />} title={errors.somethingWentWrong.title}>
      <p className="text-sm text-muted-foreground">{errors.somethingWentWrong.body}</p>
      <div className="flex flex-wrap justify-center gap-2">
        <button
          type="button"
          className={buttonStyles({ variant: "primary" })}
          onClick={() => {
            reset();
            void router.invalidate();
          }}
        >
          {common.tryAgain}
        </button>
        <Link to="/" className={buttonStyles({ variant: "secondary" })}>
          {common.goHome}
        </Link>
      </div>
      {requestId !== null && <p className="font-mono text-xs text-muted-foreground">{errors.somethingWentWrong.reference(requestId)}</p>}
    </FullPage>
  );
}
